import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Stethoscope, ShieldCheck } from "@phosphor-icons/react";

const SESSION_KEY = "hcp_gate_passed";

/**
 * Healthcare Professional Gate, Invisalign / 3M style compliance dialog.
 *
 * Wraps any route intended for dental professionals only. On first visit
 * in the session the user is asked to confirm they are a qualified
 * healthcare professional. "Yes" sets a sessionStorage flag and renders
 * the children. "No" bounces back to the homepage.
 *
 * Once confirmed, the gate stays open for the rest of the browser tab.
 */
export default function HealthcareProfessionalGate({ children }) {
    const navigate = useNavigate();
    const [confirmed, setConfirmed] = useState(
        () => typeof window !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1"
    );
    const [open, setOpen] = useState(!confirmed);

    // Re-evaluate if the user clears the flag in another tab
    useEffect(() => {
        if (!confirmed) setOpen(true);
    }, [confirmed]);

    const confirm = () => {
        sessionStorage.setItem(SESSION_KEY, "1");
        setConfirmed(true);
        setOpen(false);
    };

    const decline = () => {
        setOpen(false);
        navigate("/", { replace: true });
    };

    // If user has already confirmed in this session, just render the children
    if (confirmed) return children;

    return (
        <>
            {/* Render a placeholder so the page doesn't flash blank behind the dialog */}
            <div className="min-h-screen bg-secondary/30" aria-hidden="true" />
            <Dialog
                open={open}
                onOpenChange={(o) => { if (!o) decline(); }}
            >
                <DialogContent
                    className="max-w-md p-0 overflow-hidden rounded-2xl"
                    data-testid="hcp-gate-content"
                >
                    {/* Hero image, same one used on the provider landing for visual continuity */}
                    <div
                        className="h-44 sm:h-52 bg-cover bg-center relative"
                        style={{
                            backgroundImage:
                                "url('https://images.pexels.com/photos/4269276/pexels-photo-4269276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600')",
                        }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
                        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 text-primary text-[10px] font-bold uppercase tracking-wider">
                            <Stethoscope size={11} weight="duotone" /> For dental professionals
                        </div>
                    </div>

                    <div className="p-6">
                        <DialogTitle className="flex items-center gap-2 text-lg font-display">
                            <ShieldCheck size={20} weight="duotone" className="text-primary" />
                            Important note
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Compliance gate for healthcare professionals.
                        </DialogDescription>

                        <div className="mt-3 space-y-3 text-sm text-foreground/85 leading-relaxed">
                            <p>
                                <span className="font-bold">This section</span> and the information
                                provided in it are intended for use by <span className="font-bold">qualified
                                healthcare professionals only</span>, not by the general public.
                            </p>
                            <p>
                                By continuing, you confirm that you are a qualified healthcare
                                professional according to applicable laws and regulations in your
                                jurisdiction.
                            </p>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <Button
                                onClick={confirm}
                                className="rounded-full h-11 font-semibold"
                                data-testid="hcp-gate-confirm"
                            >
                                Yes, I'm a professional
                            </Button>
                            <Button
                                onClick={decline}
                                variant="outline"
                                className="rounded-full h-11 font-semibold"
                                data-testid="hcp-gate-decline"
                            >
                                No, take me back
                            </Button>
                        </div>

                        <p className="text-[11px] text-muted-foreground mt-4 text-center">
                            This step is required for regulatory compliance.
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
